import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

// Redux slices
import { closeModal, setModalData } from '../../appSlice'

// Actions
import { setContractData } from "./contractSlice";

// SemanticUI components 
import { Icon, Button, Modal, Form, FormInput, FormTextArea, FormGroup, FormSelect, FormCheckbox, Table, TableHeader, TableRow, TableCell, TableBody, TableHeaderCell, Container, Segment, Label, List, ListItem, ListContent, ListHeader, ListDescription, ListList, Divider } from 'semantic-ui-react'

// Components
import { mapCurrencyToOptions } from './systemdata';


/*
 * https://date-fns.org/v4.1.0/docs/addDays
 * https://www.npmjs.com/package/react-datepicker
 * https://reactdatepicker.com/#example-locale-without-global-variables
 */
import DatePicker from "react-datepicker";
import { registerLocale, setDefaultLocale } from  "react-datepicker";
import format from 'date-fns/format'
import subDays from "date-fns/subDays"
import { de } from 'date-fns/locale/de';

// Styles
import './contract.css';


registerLocale('de', de);
setDefaultLocale('de');


/**
 * 
 * @returns 
 */
const Max2ModalSpecial = () => {
  // global State
  const dispatch = useDispatch();
  const open        = useSelector((state) => state.app.modal.special.open);
  const editMode    = useSelector((state) => state.app.modal.special.editMode);
  const extra       = useSelector((state) => state.app.modal.special.data);
  const Contract    = useSelector((state) => state.contract.data.Contract);
  const systemData  = useSelector((state) => state.app.systemData);

  // i18n
  const { t } = useTranslation();

  // Edit mode?
  const readOnly = editMode ? false : true;

  // Systemdaten
  const derivedOptionsCurrency = React.useMemo(() => {
    if (!systemData || !systemData['icmp006.system_waehrung']) return [];
    const rawData = systemData['icmp006.system_waehrung'] ?? [];
    return mapCurrencyToOptions(rawData);
  }, [systemData]);
  
  // check if modal is active
  if (!open) {
    return <></>
  };

  // only float numbers allowed
  const handleKeyPressFloat = (e) => {
    if (!/^\d{0,4}(\.\d{0,2})?$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // only numbers allowed 
  const handleKeyPressNumber = (e) => {
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Handle combine extra click
  const handleCombineExtraClick = (extraId) => {
    const cextra = Contract.Extras.find((cextra) => cextra.ExtraId === extraId);
    if (cextra) {
      dispatch(setModalData({type: 'special', data: {...extra, CombinableWith: [...extra.CombinableWith, cextra]}}));
    }
  }

  // Handle remove combined extra click
  const handleRemoveCombinedExtraClick = (extraId) => {
    const newCombinableWith = extra.CombinableWith.filter((cextra) => cextra.ExtraId !== extraId);
    dispatch(setModalData({type: 'special', data: {...extra, CombinableWith: newCombinableWith}}));
  }

  // Handle apply extra click
  const handleApplyExtraClick = () => {
    dispatch(setContractData({target: 'Special', ExtraId: extra.ExtraId, value: extra}));
    dispatch(closeModal('special'));
  }

  let currencyCode = '';
  derivedOptionsCurrency.forEach((curr) => {
    if (curr.value === Contract.Currency) {
      currencyCode = curr.code;
    }
  });

  //
  const optionsValueType = [
    { key: '%', text: '%', value: '%' },
    { key: 'DAY', text: t('contract.specials.options.currencyPerDay', { currency: currencyCode }), value: 'DAY' },
    { key: 'WEEK', text: t('contract.specials.options.currencyPerWeek', { currency: currencyCode }), value: 'WEEK' },
    { key: 'FIX', text: t('contract.specials.options.currencyFixed', { currency: currencyCode }), value: 'FIX' },
  ]
  const optionsArrival = [
    { key: 'A', text: t('contract.specials.options.beforeArrival'), value: 'A' },
    { key: 'F', text: t('contract.specials.options.untilArrival'), value: 'F' }
  ]
  const optionsRelation = [
    { key: 'A', text: t('contract.specials.options.stayRelated'), value: 'A' },
    { key: 'F', text: t('contract.specials.options.arrivalRelated'), value: 'F' }
  ]
  const optionsBoardOptions = [ 
    { key: 'ABT', text: t('contract.specials.options.allBoardTypes'), value: 'ABT' },
    { key: 'SBT', text: t('contract.specials.options.selectedBoardTypes'), value: 'SBT' },
    { key: 'BBO', text: t('contract.specials.options.onlyBasicBoard'), value: 'BBO' }
  ]
  const optionsCostType = [ 
    { key: 'SIL', text: t('contract.specials.options.newYearsEve'), value: 'SIL' },
    { key: 'WEI', text: t('contract.specials.options.christmasEve'), value: 'WEI' },
    { key: 'SON', text: t('contract.specials.options.addOns'), value: 'SON' },
  ]
  const optionsCostPer = [ 
    { key: 'ZU', text: t('contract.specials.options.pricePerPerson', { currency: currencyCode }), value: 'ZU' },
    { key: 'PP', text: t('contract.specials.options.additionalCostPerPerson', { currency: currencyCode }), value: 'PP' },
  ]
  const optionsRegulation = [ 
    { key: 'SO', text: t('contract.specials.options.removeLastNights'), value: 'SO' },
    { key: 'SB', text: t('contract.specials.options.removeCheapestNights'), value: 'SB' },
  ]
  
  // Option values for room select
  const optionsRooms = Contract.Rooms?.map((room) => {
    return { key: room.RoomId, text: room.RoomCode, value: room.RoomCode }
  });

  // Option values for board select 
  let optionsBoards = [];
  Contract.Rooms?.forEach((room) => {
    if (room.Board && !optionsBoards.some((item) => item.value === room.Board)) {
      optionsBoards.push({ key: room.Board, text: room.Board, value: room.Board });
    }
  });
  Contract.Boards?.forEach((board) => {
    if (board.Board && !optionsBoards.some((item) => item.value === board.Board)) {
      optionsBoards.push({ key: board.Board, text: board.Board, value: board.Board });
    }
  });

  // Build list of combined extra types
  let combinedExtraTypes = [];
  if (extra.CombinableWith && extra.CombinableWith.length > 0) {
    extra.CombinableWith.forEach((cextra) => {
      if (cextra.ExtraType && !combinedExtraTypes.some((item) => item.value === cextra.ExtraType)) {
        combinedExtraTypes.push({ key: cextra.ExtraType, text: cextra.ExtraType, value: cextra.ExtraType });
      }
    });
  }

  // Build list of extras available to combine with
  let availableExtras = [];
  Contract.Extras.forEach((cextra) => {
    if (extra.ExtraId !== cextra.ExtraId && (typeof extra.CombinableWith === 'undefined' || !extra.CombinableWith.some((item) => item === cextra.ExtraId))) {
      availableExtras.push({ key: cextra.ExtraId, text: cextra.ExtraId, value: cextra.ExtraId });
    }
  });

  // Build short info list of all extras 
  let extraShortInfo = {};
  Contract.Extras.forEach((extra) => {
    let txt = "";

    // value
    switch (extra.ExtraType) {
      case 'Early Booking Special':
      case 'Special Supplement':
      case 'Special Reduction':
        txt = `${extra.ExtraType} (${extra.ExtraId}): ${extra.Value} ${optionsValueType.find(option => option.value === extra.ValueType)?.text}`;
        break;

      case 'Free Night Special':
        txt = `${extra.ExtraType} (${extra.ExtraId}): ${extra.FreeNight?.Stay} Tage = ${extra.FreeNight?.Pay} Tage, ${optionsRegulation.find(option => option.value === extra.FreeNight?.Regulation)?.text}`;
        break;

      case 'Additional Costs':
        txt = `${extra.ExtraType} (${extra.ExtraId}): ${optionsCostType.find(option => option.value === extra.CostType)?.text} ${extra.Value} ${optionsCostPer.find(option => option.value === extra.CostPer)?.text}`;
        break;

      case 'Special Rate':
        txt = `${extra.ExtraType} (${extra.ExtraId}): ${extra.Value} ${optionsValueType.find(option => option.value === extra.ValueType)?.text}`;
        break;
      default: break;
    }

    // Dates
    txt += ", ";
    if (extra.Restrictions?.Dates && extra.Restrictions?.Dates.length > 0) {
      txt += extra.Restrictions.Dates.map((date) => {
        return `${format(new Date(date.Period.Start), 'dd.MM')} - ${format(new Date(date.Period.End), 'dd.MM.yyyy')}`;
      }).join(', ');
    } else {
      txt += "keine Zeiträume";
    }

    // BookingWindow
    if (extra.Restrictions?.Bookings && extra.Restrictions?.Bookings.length > 0) {
      txt += ", Buchungen: ";
      txt += extra.Restrictions.Bookings.map((booking) => {
        if (booking?.Period?.Start && booking?.Period?.End) {
          return `${format(new Date(booking.Period.Start), 'dd.MM')} - ${format(new Date(booking.Period.End), 'dd.MM.yyyy')}`;
        } else {
          if (booking?.Period?.Start) {
            return `${format(new Date(booking.Period.Start), 'dd.MM')}`;
          } else {
            if (booking?.Period?.End) {
              return `${format(new Date(booking.Period.End), 'dd.MM.yyyy')}`;
            } else {
              return "";
            }
          }
        }
      }).join(', ');
    } else {
      txt += "";
    }

    // Rooms
    if (extra.Restrictions?.Rooms && extra.Restrictions.Rooms.length > 0) {
      txt += ", Zimmer: ";
      txt += extra.Restrictions.Rooms.join(', ');
    } else {
      txt += "";
    }

    extraShortInfo[extra.ExtraId] = txt
  });

  // Build reduction rows
  const reductions = extra.Reductions?.map((reduction)=> {
    const reductionValueType = reduction?.Percent ? 'P' : 'F';
    return (
      <TableRow>
        <TableCell>
          {readOnly ? (
            <span>{reduction.ChildNumber === 0 ? '-' : reduction.ChildNumber + '.'}</span>
          ) : (
            <select readOnly={readOnly} value={reduction.ChildNumber} className="select_small" onChange={(e) => {}}>
              <option>bitte wählen</option>
              <option key='0' value='0'>-</option>
              <option key='1' value='1'>1.</option>
              <option key='2' value='2'>2.</option>
              <option key='3' value='3'>3.</option>
              <option key='4' value='4'>4.</option>
            </select>
          )}
        </TableCell>
        <TableCell>{readOnly ? (<span>{reduction.ChildAgeFrom}</span>) : (<FormInput readOnly={readOnly} className="small" style={{width: "75px", textAlign: "right"}} maxLength={2} type="number" onKeyPress={handleKeyPressNumber} value={reduction.ChildAgeFrom} placeholder="0"  onChange={(e) => {}} />)}</TableCell>
        <TableCell>{readOnly ? (<span>{reduction.ChildAgeTo}</span>) : (<FormInput readOnly={readOnly} className="small" style={{width: "75px", textAlign: "right"}} maxLength={2} type="number" onKeyPress={handleKeyPressNumber} value={reduction.ChildAgeTo} placeholder="99" onChange={(e) => {}} />)}</TableCell>
        <TableCell>{readOnly ? (<span>{reductionValueType === 'P' ? reduction?.Percent : reduction?.Amount}</span>) : (<FormInput readOnly={readOnly} className="small" style={{width: "75px", textAlign: "right"}} maxLength={7} type="number" onKeyPress={handleKeyPressFloat}  value='' placeholder={reductionValueType === 'P' ? reduction?.Percent : reduction?.Amount} onChange={(e) => {}} />)}</TableCell>
        <TableCell>
          {readOnly ? (
            <span>{reductionValueType === 'P' ? '%' : '€'}</span>
          ) : (
            <select readOnly={readOnly} value={reductionValueType} className="select_small" onChange={(e) => {}}>
              <option>bitte wählen</option>
              <option key='P' value='P'>%</option>
              <option key='F' value='F'>€</option>
            </select>
          )}
        </TableCell>
        <TableCell>
          {!readOnly && (
            <>
            <Button basic circular icon size="tiny" onClick={() => {}}><Icon name='minus' /></Button>
            <Button basic circular icon size="tiny" onClick={() => {}}><Icon name='add' /></Button>
            </>
          )}
        </TableCell>
      </TableRow>
    )
  });

  // styles
  const detailSecondaryLabel  = { display: 'flex', justifyContent: 'right', alignItems: 'center', color: '#ccc' }

  return (
    <Modal
      open={open}
      onClose={() => dispatch(closeModal('special'))}
      size="large" 
    >
      <Modal.Header>
        <span className='meta'>{extra.ExtraType}:</span>{extra.ExtraId}
      </Modal.Header>



      {readOnly ? (
        <Modal.Content scrolling>
          
          <Segment textAlign='left' style={{ margin: '20px' }}>
            <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{t('contract.specials.modal.textRepresentation')}</span></Label>
            <Container>{extra.ExtraText}</Container>
          </Segment>

          <Segment textAlign='left' style={{ margin: '20px' }}>
            <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{t('contract.specials.modal.details')}</span></Label>
            <Container>

              <Table basic='very' celled >
                <TableBody>
                    <TableRow>
                      {(extra.ExtraType === 'Early Booking Special' || extra.ExtraType === 'Special Supplement' || extra.ExtraType === 'Special Reduction') && (
                        <>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.value')}</TableCell>
                        <TableCell>
                          {extra.Value}
                          &nbsp;
                          {optionsValueType.map(option => {
                            if (option.value === extra.ValueType) {
                              return <span key={option.value}>{option.text}</span>
                            }
                            return null;
                          })}
                        </TableCell>
                        </>
                      )}
                      {(extra.ExtraType === 'Special Rate') && (
                        <>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.value')}</TableCell>
                        <TableCell>
                          {extra.Value}
                        </TableCell>
                        </>
                      )}
                      {extra.ExtraType === 'Additional Costs' && (
                        <>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.value')}</TableCell>
                        <TableCell>
                          {optionsCostType.map(option => {
                            if (option.value === extra.CostType) {
                              return <span key={option.value}>{option.text}</span>
                            }
                            return null;
                          })}
                          &nbsp;
                          {extra.Value}
                          &nbsp;
                          {optionsCostPer.map(option => {
                            if (option.value === extra.CostPer) {
                              return <span key={option.value}>{option.text}</span>
                            }
                            return null;
                          })}
                        </TableCell>
                        </>
                      )}

                      {extra.ExtraType === 'Free Night Special' && (
                        <>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.days')}</TableCell>
                        <TableCell>
                          {extra.FreeNight?.Stay}&nbsp;=&nbsp;{extra.FreeNight?.Pay}
                          ,&nbsp;
                          {optionsRegulation.map(option => {
                            if (option.value === extra.FreeNight?.Regulation) {
                              return <span key={option.value}>{option.text}</span>
                            }
                            return null;
                          })}
                          {extra.FreeNight.Cummulation
                            ? ` (${t('contract.specials.modal.cumulable')})`
                            : ` (${t('contract.specials.common.notCumulable') || 'nicht kummulierbar'})`}
                        </TableCell>
                        </>
                      )}
                    </TableRow>

                    {extra.Information && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.information')}</TableCell>
                        <TableCell>{extra.Information}</TableCell>
                      </TableRow>
                    )}

                    {extra.Restrictions?.Dates.length > 0 && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.periods')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Dates.map((specialDate) => (
                            <>{format(new Date(specialDate.Period.Start), 'dd.MM')} - {format(new Date(specialDate.Period.End), 'dd.MM.yyyy')}<br /></>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {extra.Restrictions?.Bookings > 0 && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.bookings')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Bookings.map((specialDate) => (
                            <>{format(new Date(specialDate.Period.Start), 'dd.MM')} - {format(new Date(specialDate.Period.End), 'dd.MM.yyyy')}<br /></>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {extra.Restrictions?.Rooms.length > 0 && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.rooms')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Rooms.map((room) => (
                            <span key={room} style={{marginRight: '10px'}}>{room}</span>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {extra.Restrictions?.Boards.length > 0 && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.boards')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Boards.map((board) => (
                            <span key={board} style={{marginRight: '10px'}}>{board}</span>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {(extra.ExtraType === 'Free Night Special' || extra.ExtraType === 'Special Supplement' || extra.ExtraType === 'Special Reduction' || extra.ExtraType === 'Special Rate') && (
                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.weekdays')}</TableCell>
                      <TableCell>
                        {extra.Restrictions?.Weekdays && (
                          <>
                          <span>Mo.: {extra.Restrictions.Weekdays.Mon ? t('common.yes') : t('common.no')}</span><br />
                          <span>Di.: {extra.Restrictions.Weekdays.Tue ? t('common.yes') : t('common.no')}</span><br />
                          <span>Mi.: {extra.Restrictions.Weekdays.Wed ? t('common.yes') : t('common.no')}</span><br />
                          <span>Do.: {extra.Restrictions.Weekdays.Thu ? t('common.yes') : t('common.no')}</span><br />
                          <span>Fr.: {extra.Restrictions.Weekdays.Fri ? t('common.yes') : t('common.no')}</span><br />
                          <span>Sa.: {extra.Restrictions.Weekdays.Sat ? t('common.yes') : t('common.no')}</span><br />
                          <span>So.: {extra.Restrictions.Weekdays.Sun ? t('common.yes') : t('common.no')}</span><br />
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    )}

                    {(extra.Restrictions?.Bookings[0].MinBookingOffset.Value || extra.Restrictions?.BoardRestrictionOptions || extra.Relation) && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.validity')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Bookings[0].MinBookingOffset.Value > 0 && (
                            <>
                            {extra.Restrictions?.Bookings[0].MinBookingOffset.Type === 'B'
                              ? <span>{t('contract.specials.modal.minBookingOffsetBeforeArrival', { days: extra.Restrictions?.Bookings[0].MinBookingOffset.Value })}</span>
                              : <span style={{paddingLeft: '13px'}}>{t('contract.specials.modal.minBookingOffsetAfterArrival', { days: extra.Restrictions?.Bookings[0].MinBookingOffset.Value })}</span>}
                            <br />
                            </>
                          )}
                          {optionsBoardOptions.map(option => {
                            if (option.value === extra.Restrictions?.BoardRestrictionOptions) {
                              return <span key={option.value}>{option.text}</span>
                            }
                            return null;
                          })}
                          <br />
                          {(extra.ExtraType === 'Early Booking Special' || extra.ExtraType === 'Special Reduction' || extra.ExtraType === 'Special Rate') && (
                            <>
                            {optionsRelation.map(option => {
                              if (option.value === extra.Relation) {
                                return <span key={option.value}>{option.text}</span>
                              }
                              return null;
                            })}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )}

                    {(extra.Restrictions?.Stay?.Min > 0 || extra.Restrictions?.Stay?.Max > 0) && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.stay')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Stay?.Min > 0 && (
                            <>
                            <span>{t('contract.specials.modal.minStay', { days: extra.Restrictions?.Stay?.Min })}</span>&nbsp;&nbsp;
                            </>
                          )}
                          {extra.Restrictions?.Stay?.Max > 0 && (
                            <>
                            <span>{t('contract.specials.modal.maxStay', { days: extra.Restrictions?.Stay?.Max })}</span>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )}


                    {extra.Reduction && (
                      <TableRow>
                        <TableCell textAlign='right'>{t('contract.specials.modal.reductions')}</TableCell>
                        <TableCell>
                          <Table basic='very' celled collapsing>
                            <TableHeader>
                              <TableRow>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.childAdult')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.ageFrom')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.ageTo')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.reduction')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.type')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}></TableHeaderCell>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {reductions}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}


                </TableBody>
              </Table>
            </Container>
          </Segment>

          <Segment textAlign='left' style={{ margin: '20px' }}>
            <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{t('contract.specials.modal.combinability')}</span></Label>
            <Container>
              <span>{t('contract.specials.modal.combinableWithFollowing')}</span>
              <Divider />
              <ListList>
                {extra.CombinableWith.map((cextra, cextraIndex) => (
                  <ListItem key={cextraIndex} verticalAlign='middle'>
                    <ListContent>
                      <ListDescription>{extraShortInfo[cextra]}</ListDescription>
                    </ListContent>
                  </ListItem>
                ))}
              </ListList>
            </Container>
          </Segment>
        </Modal.Content>

















      ) : (
        <Modal.Content scrolling>
          <Segment textAlign='left' style={{ margin: '20px' }}>
            <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{t('contract.specials.modal.textRepresentation')}</span></Label>
            <Container>
              <Form>
                <FormTextArea placeholder={t('contract.specials.modal.textPlaceholder')} value={extra.ExtraText} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, ExtraText: data.value}}))} />
              </Form>
            </Container>
          </Segment>

          <Segment textAlign='left' style={{ margin: '20px' }}>
            <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{t('contract.specials.modal.details')}</span></Label>
            <Container>
              <Form>
                <Table basic='very' celled >
                  <TableBody>
                    <TableRow>
                      {(extra.ExtraType === 'Early Booking Special' || extra.ExtraType === 'Special Supplement' || extra.ExtraType === 'Special Reduction') && (
                        <>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.value')}</TableCell>
                        <TableCell>
                          <FormGroup style={{marginBottom: '0px'}}>
                            <Form.Field>
                              <FormInput 
                                style       = {{width: "75px", textAlign: "right"}} 
                                type        = "number" 
                                maxLength   = {7} 
                                value       = {extra.Value} 
                                placeholder = '' 
                                onKeyPress  = {handleKeyPressFloat} 
                                onChange    = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Value: data.value}}))}
                              />
                            </Form.Field>
                            <Form.Field>
                              <FormSelect value={extra.ValueType} options={optionsValueType} placeholder={t('common.pleaseSelect')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, ValueType: data.value}}))} />
                            </Form.Field>
                          </FormGroup>
                        </TableCell>
                        </>
                      )}
                      {(extra.ExtraType === 'Special Rate') && (
                        <>
                        <TableCell collapsing textAlign='right'>Wert</TableCell>
                        <TableCell>
                          <FormInput
                            style       = {{width: "75px", textAlign: "right"}} 
                            type        = "number" 
                            maxLength   = {7} 
                            value       = {extra.Value} 
                            placeholder = '' 
                            onKeyPress  = {handleKeyPressFloat} 
                            onChange    = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Value: data.value}}))}
                          />
                        </TableCell>
                        </>
                      )}
                      {extra.ExtraType === 'Additional Costs' && (
                        <>
                        <TableCell collapsing textAlign='right'>Wert</TableCell>
                        <TableCell>
                          <FormGroup style={{marginBottom: '0px'}}>
                            <Form.Field>
                              <FormSelect value={extra.CostType} options={optionsCostType} placeholder={t('common.pleaseSelect')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, CostType: data.value}}))} />
                            </Form.Field>
                            <Form.Field>
                              <FormInput
                                style         = {{width: "75px", textAlign: "right"}} 
                                type          = "number" 
                                maxLength     = {7} 
                                value         = {extra.Value} 
                                placeholder   = '' 
                                onKeyPress    = {handleKeyPressFloat} 
                                onChange      = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Value: data.value}}))}
                              />
                            </Form.Field>
                            <Form.Field>
                              <FormSelect value={extra.CostPer} options={optionsCostPer} placeholder={t('common.pleaseSelect')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Value: data.value}}))} />
                            </Form.Field>
                          </FormGroup>
                        </TableCell>
                        </>
                      )}

                      {extra.ExtraType === 'Free Night Special' && (
                        <>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.days')}</TableCell>
                        <TableCell>
                          <FormGroup style={{marginBottom: '0px'}}>
                            <Form.Field>
                              <FormInput
                                style         = {{width: "75px", textAlign: "right"}} 
                                type          = "number" 
                                maxLength     = {7} 
                                value         = {extra.FreeNight?.Stay} 
                                placeholder   = '' 
                                onKeyPress    = {handleKeyPressFloat} 
                                onChange      = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, FreeNight: {...extra.FreeNight, Stay: data.value}}}))}
                              />
                            </Form.Field>
                            <Form.Field style={{paddingTop: "10px"}}>=</Form.Field>
                            <Form.Field>
                              <FormInput 
                                style         = {{width: "75px", textAlign: "right"}} 
                                type          = "number" 
                                maxLength     = {7} 
                                value         = {extra.FreeNight?.Pay} 
                                placeholder   = '' 
                                onKeyPress    = {handleKeyPressFloat} 
                                onChange      = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, FreeNight: {...extra.FreeNight, Pay: data.value}}}))}
                              />
                            </Form.Field>
                            <Form.Field>
                              <FormSelect value={extra.FreeNight?.Regulation} options={optionsRegulation} placeholder={t('common.pleaseSelect')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, FreeNight: {...extra.FreeNight, Regulation: data.value}}}))} />
                            </Form.Field>
                            <Form.Field style={{paddingTop: "8px"}}>
                              <FormCheckbox toggle checked={extra.FreeNight?.Cummulation} label={t('contract.specials.modal.cumulable')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, FreeNight: {...extra.FreeNight, Cummulation: !extra.FreeNight?.Cummulation}}}))} />
                            </Form.Field>
                          </FormGroup>
                        </TableCell>
                        </>
                      )}
                    </TableRow>

                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.information')}</TableCell>
                      <TableCell><FormInput style={{width: "555px"}} value={extra.Information} placeholder={t('contract.specials.modal.infoPlaceholder')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Information: data.value}}))} /></TableCell>
                    </TableRow>

                    {extra.Restrictions?.Dates.length > 0 && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.periods')}</TableCell>
                        <TableCell>
                          {extra.Restrictions?.Dates.map((specialDate, specialDateIndex) => (
                            <FormGroup style={{marginBottom: '5px'}}>
                              <Form.Field>
                                <DatePicker
                                  showWeekNumbers
                                  selectsStart
                                  selected    = {specialDate.Period.Start}
                                  startDate   = {specialDate.Period.Start}
                                  endDate     = {specialDate.Period.End}
                                  dateFormat  = "dd.MM.yyyy"
                                  placeholder = {t('contract.specials.modal.datePlaceholder')}
                                  onChange    = {(date) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Dates: extra.Restrictions.Dates.map((d, idx) => idx === specialDateIndex ? {...d, Period: {...d.Period, Start: format(date, "yyyy-MM-dd")}} : d)}}}))}
                                  customInput = {<FormInput inline icon='calendar alternate' iconPosition='left' label={t('contract.specials.modal.from')} placeholder={t('contract.specials.modal.datePlaceholder')} style={{width: "130px", marginRight: '20px'}} />}
                                  excludeDateIntervals = {[
                                    { start: new Date("1970-01-01"), end: subDays(new Date(Contract.Period.Start), 1) },
                                    { start: new Date(Contract.Period.End), end: new Date("9999-12-31") },
                                  ]}
                                />
                                <DatePicker
                                  showWeekNumbers
                                  selectsEnd
                                  selected    = {specialDate.Period.End}
                                  startDate   = {specialDate.Period.Start}
                                  endDate     = {specialDate.Period.End}
                                  minDate     = {specialDate.Period.Start}
                                  dateFormat  = "dd.MM.yyyy"
                                  placeholder = 'TT.MM.JJJJ'
                                  onChange    = {(date) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Dates: extra.Restrictions.Dates.map((d, idx) => idx === specialDateIndex ? {...d, Period: {...d.Period, End: format(date, "yyyy-MM-dd")}} : d)}}}))}
                                  customInput = {<FormInput inline icon='calendar alternate' iconPosition='left' label={t('contract.specials.modal.to')} placeholder={t('contract.specials.modal.datePlaceholder')} style={{width: "130px", marginRight: '20px'}} />}
                                  excludeDateIntervals = {[
                                    { start: new Date("1970-01-01"), end: subDays(new Date(Contract.Period.Start), 1) },
                                    { start: new Date(Contract.Period.End), end: new Date("9999-12-31") },
                                    { start: subDays(new Date(Contract.Period.Start), 1), end: subDays(specialDate.Period.End, 1) },
                                  ]}
                                />
                              </Form.Field>
                              <Form.Field>
                                {specialDateIndex > 0 && (
                                  <Button basic circular icon size="tiny" onClick={() => {}}><Icon name='minus' /></Button>
                                )}
                              </Form.Field>
                              {specialDateIndex === (extra.Restrictions?.Dates.length - 1) && (
                                <Form.Field>
                                  <Button basic circular icon size="tiny" onClick={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Dates: [...extra.Restrictions.Dates, {Period: {Start: extra.Restrictions?.Dates[extra.Restrictions?.Dates.length-1].Period.End, End: extra.Restrictions?.Dates[extra.Restrictions?.Dates.length-1].Period.End}, Relation: 'STAY'}]}}}))}><Icon name='add' /></Button>
                                </Form.Field>
                              )}
                            </FormGroup>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.bookings')}</TableCell>
                      <TableCell>
                      {extra.Restrictions?.Bookings.map((specialDate, specialDateIndex) => (
                        <>
                        <Form.Field>
                          <DatePicker
                            showWeekNumbers
                            selectsStart
                            selected    = {specialDate?.Period?.Start}
                            startDate   = {specialDate?.Period?.Start}
                            endDate     = {specialDate?.Period?.End}
                            dateFormat  = "dd.MM.yyyy"
                            placeholder = {t('contract.specials.modal.datePlaceholder')}
                            onChange    = {(date) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Bookings: extra.Restrictions.Bookings.map((d, idx) => idx === specialDateIndex ? {...d, Period: {...d.Period, Start: format(date, "yyyy-MM-dd")}} : d)}}}))}
                            customInput = {<FormInput inline icon='calendar alternate' iconPosition='left' label={t('contract.specials.modal.from')} placeholder={t('contract.specials.modal.datePlaceholder')} style={{width: "130px", marginRight: '20px'}} />}
                            excludeDateIntervals = {[
                              { start: new Date("1970-01-01"), end: subDays(new Date(Contract.Period.Start), 1) },
                              { start: new Date(Contract.Period.End), end: new Date("9999-12-31") },
                            ]}
                          />
                          <DatePicker
                            showWeekNumbers
                            selectsEnd
                            selected    = {specialDate?.Perio?.End}
                            startDate   = {specialDate?.Period?.Start}
                            endDate     = {specialDate?.Period?.End}
                            minDate     = {specialDate?.Period?.Start}
                            dateFormat  = "dd.MM.yyyy"
                            placeholder = 'TT.MM.JJJJ'
                            onChange    = {(date) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Bookings: extra.Restrictions.Bookings.map((d, idx) => idx === specialDateIndex ? {...d, Period: {...d.Period, End: format(date, "yyyy-MM-dd")}} : d)}}}))}
                            customInput = {<FormInput inline icon='calendar alternate' iconPosition='left' label={t('contract.specials.modal.to')} placeholder={t('contract.specials.modal.datePlaceholder')} style={{width: "130px", marginRight: '20px'}} />}
                            excludeDateIntervals = {[
                              { start: new Date("1970-01-01"), end: subDays(new Date(Contract.Period.Start), 1) },
                              { start: new Date(Contract.Period.End), end: new Date("9999-12-31") },
                              { start: subDays(new Date(Contract.Period.Start), 1), end: subDays(specialDate?.Period?.End, 1) },
                            ]}
                          />
                        </Form.Field>
                        </>
                      ))}
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.rooms')}</TableCell>
                      <TableCell>
                        <FormSelect multiple value={extra.Restrictions?.Rooms} options={optionsRooms} placeholder={t('common.pleaseSelect')} onChange = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Rooms: data.value}}}))} />
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.boards')}</TableCell>
                      <TableCell>
                        <FormSelect multiple value={extra.Restrictions?.Boards} options={optionsBoards} placeholder={t('common.pleaseSelect')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Boards: data.value}}}))} />
                      </TableCell>
                    </TableRow>

                    {(extra.ExtraType === 'Free Night Special' || extra.ExtraType === 'Special Supplement' || extra.ExtraType === 'Special Reduction' || extra.ExtraType === 'Special Rate') && (
                      <TableRow>
                        <TableCell collapsing textAlign='right'>{t('contract.specials.modal.weekdays')}</TableCell>
                        <TableCell>
                          <FormGroup style={{marginBottom: '0px'}}>
                            <Form.Field style={detailSecondaryLabel}>Mo.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Mon} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Mon: !extra.Restrictions?.Weekdays?.Mon}}}}))} /></Form.Field>
                            <Form.Field style={detailSecondaryLabel}>Di.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Tue} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Tue: !extra.Restrictions?.Weekdays?.Tue}}}}))} /></Form.Field>
                            <Form.Field style={detailSecondaryLabel}>Mi.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Wed} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Wed: !extra.Restrictions?.Weekdays?.Wed}}}}))} /></Form.Field>
                            <Form.Field style={detailSecondaryLabel}>Do.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Thu} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Thu: !extra.Restrictions?.Weekdays?.Thu}}}}))} /></Form.Field>
                            <Form.Field style={detailSecondaryLabel}>Fr.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Fri} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Fri: !extra.Restrictions?.Weekdays?.Fri}}}}))} /></Form.Field>
                            <Form.Field style={detailSecondaryLabel}>Sa.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Sat} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Sat: !extra.Restrictions?.Weekdays?.Sat}}}}))} /></Form.Field>
                            <Form.Field style={detailSecondaryLabel}>So.</Form.Field>
                            <Form.Field><FormCheckbox toggle checked={extra.Restrictions?.Weekdays?.Sun} onChange={() => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Weekdays: {...extra.Restrictions.Weekdays, Sun: !extra.Restrictions?.Weekdays?.Sun}}}}))} /></Form.Field>
                          </FormGroup>
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.validity')}</TableCell>
                      <TableCell>
                        <FormGroup style={{marginBottom: '0px'}}>
                          <Form.Field style={detailSecondaryLabel}><span style={{paddingLeft: '13px'}}>{t('contract.specials.modal.bisAb')}</span></Form.Field>
                          <Form.Field>
                            <FormInput style={{width: "75px"}} maxLength={3} type="number" onKeyPress={handleKeyPressNumber} value={extra.Restrictions?.Bookings[0]?.MinBookingOffset?.Value} placeholder={t('contract.specials.modal.days')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Bookings: [{Period: {Start: extra.Restrictions.Bookings[0].Period.Start, End: extra.Restrictions.Bookings[0].Period.End}, MinBookingOffset: {...extra.Restrictions.Bookings[0].MinBookingOffset, Value: data.value}}]}}}))} />
                          </Form.Field>
                          <Form.Field style={detailSecondaryLabel}>{t('contract.specials.modal.days')}</Form.Field>
                          <Form.Field>
                            <FormSelect value={extra.Restrictions?.Bookings[0]?.MinBookingOffset?.Type} options={optionsArrival} placeholder={t('common.pleaseSelect')} onChange={(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Bookings: [{Period: {Start: extra.Restrictions.Bookings[0].Period.Start, End: extra.Restrictions.Bookings[0].Period.End}, MinBookingOffset: {...extra.Restrictions.Bookings[0].MinBookingOffset, Type: data.value}}]}}}))} />
                          </Form.Field>
                          <Form.Field style={detailSecondaryLabel}>&nbsp;|&nbsp;</Form.Field>
                          <Form.Field>
                            <FormSelect value={extra.Restrictions?.BoardRestrictionOptions} options={optionsBoardOptions} placeholder={t('common.pleaseSelect')} onChange = {(e, data) => { console.log('[extra.Restrictions]', extra.Restrictions); console.log('[data.value]', data.value); dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, BoardRestrictionOptions: data.value}}})); } } />
                          </Form.Field>
                          {(extra.ExtraType === 'Early Booking Special' || extra.ExtraType === 'Special Reduction' || extra.ExtraType === 'Special Rate') && (
                            <>
                            <Form.Field style={detailSecondaryLabel}>&nbsp;|&nbsp;</Form.Field>
                            <Form.Field>
                              <FormSelect value={extra.Relation} options={optionsRelation} placeholder={t('common.pleaseSelect')} onChange = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Relation: data.value}}))} />
                            </Form.Field>
                            </>
                          )}
                        </FormGroup>
                      </TableCell>
                    </TableRow>
                    


                    <TableRow>
                      <TableCell collapsing textAlign='right'>{t('contract.specials.modal.stay')}</TableCell>
                      <TableCell>
                        <FormGroup style={{marginBottom: '0px'}}>
                          <Form.Field style={detailSecondaryLabel}><span style={{paddingLeft: '22px'}}>min</span></Form.Field>
                          <Form.Field>
                            <FormInput style={{width: "75px"}} maxLength={3} type="number" onKeyPress={handleKeyPressNumber} value={extra.Restrictions?.Stay?.Min} placeholder={t('contract.specials.modal.days')} onChange = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Stay: {...extra.Restrictions.Stay, Min: data.value}}}}))} />
                          </Form.Field>
                          <Form.Field style={detailSecondaryLabel}>max</Form.Field>
                          <Form.Field>
                            <FormInput style={{width: "75px"}} maxLength={3} type="number" onKeyPress={handleKeyPressNumber} value={extra.Restrictions?.Stay?.Max} placeholder={t('contract.specials.modal.days')} onChange = {(e, data) => dispatch(setModalData({type: 'special', data: {...extra, Restrictions: {...extra.Restrictions, Stay: {...extra.Restrictions.Stay, Max: data.value}}}}))} />
                          </Form.Field>
                          <Form.Field style={detailSecondaryLabel}>{t('contract.specials.modal.days')}</Form.Field>
                        </FormGroup>
                      </TableCell>
                    </TableRow>


                    
                    <TableRow>
                      <TableCell textAlign='right'>{t('contract.specials.modal.reductions')}</TableCell>
                      <TableCell>
                        <Table basic='very' celled collapsing>
                          {extra.Reductions?.length > 0 && (
                            <TableHeader>
                              <TableRow>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.childAdult')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.ageFrom')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.ageTo')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.reduction')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}>{t('contract.specials.reductions.header.type')}</TableHeaderCell>
                                <TableHeaderCell style={{borderBottom: '1px solid rgba(255, 255, 255, .31)'}}></TableHeaderCell>
                              </TableRow>
                            </TableHeader>
                          )}
                          <TableBody>
                            {reductions}
                            <TableRow>
                              <TableCell collapsing textAlign='center' colspan='6'> 
                                <Button basic circular icon size="tiny" onClick={() => {}}><Icon name='add' /></Button>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>

                  </TableBody>
                </Table>
              </Form>
            </Container>
          </Segment>

          <Segment textAlign='left' style={{ margin: '20px' }}>
            <Label circular style={{marginBottom: '20px', background: 'transparent'}}><span style={{fontSize: '1.4em', margin: '10px'}}>{t('contract.specials.modal.combinability')}</span></Label>
            <Container>
              <span>{t('contract.specials.modal.combinableWithFollowing')}</span>
              <Divider />
              <List>
                {extra.CombinableWith && extra.CombinableWith.map((cextra, cextraIndex) => (
                  <ListItem key={cextraIndex} verticalAlign='middle'>
                    <ListContent floated='right'>
                      <Button basic circular icon size="tiny" title={t('contract.specials.modal.removeCombinedTooltip')} onClick={() => handleRemoveCombinedExtraClick(cextra)}><Icon name='minus' /></Button>
                    </ListContent>
                    <ListContent>
                      <ListDescription>{extraShortInfo[cextra]}</ListDescription>
                    </ListContent>
                  </ListItem>
                ))}
              </List>

              <br />
              <br />

              
              <span>{t('contract.specials.modal.availableSpecials')}</span>
              <Divider />
              <Container style={{marginTop: '10px'}}>
                <List verticalAlign='middle'>
                  {availableExtras.map((availableExtra, availableExtraIndex) => (
                    <ListItem key={availableExtraIndex}>
                      <ListContent floated='right'>
                        <Button basic circular icon size="tiny" title={t('contract.specials.modal.combineTooltip')} onClick={() => handleCombineExtraClick(availableExtra.value)}><Icon name='level up alternate' /></Button>
                      </ListContent>
                      <ListContent>
                        <ListHeader>{availableExtra.ExtraId}</ListHeader>
                        <ListDescription>{extraShortInfo[availableExtra.value]}</ListDescription>
                      </ListContent>
                    </ListItem>
                  ))}
                </List>
              </Container>
            </Container>
          </Segment>
        </Modal.Content>
      )}
      
      <Modal.Actions>
        <Button basic circular icon labelPosition='left' onClick={() => dispatch(closeModal('special'))}>
            <Icon name='times' />
            {t('common.close')}
          </Button>
          {!readOnly && (
            <Button primary circular icon labelPosition='left' onClick={() => handleApplyExtraClick()}>
              <Icon name='check' />
              {t('contract.specials.actions.apply')}
            </Button>
          )}
      </Modal.Actions>
    </Modal>
  );
}

export default Max2ModalSpecial;
